import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ChevronRight, Search, X } from "lucide-react";
import { IconChevronDown } from "icons";
import { MenuItem, MenuSection } from "./useMenuItems";
import GlideMenu from "./GlideMenu";
import NavFlyout from "./NavFlyout";
import { PATHS } from "router/config/paths";

// First navigable link under a row (depth-first). For a dropdown that's its first child's link;
// for a leaf it's the row itself. Used so clicking a dropdown opens its first link.
const firstNavigableDescendant = (item: MenuItem): MenuItem | null => {
  if (item.children?.length) {
    for (const child of item.children) {
      const found = firstNavigableDescendant(child);
      if (found) return found;
    }

    return null;
  }

  return item.path ? item : null;
};

/**
 * A section's title with a search beside it (recent chats): the magnifier opens a field over the title that
 * filters the section's rows by name; Escape or × closes it and clears the query.
 */
const SectionSearch = ({ title, query, onQuery }: { title: string; query: string | null; onQuery: (query: string | null) => void }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const open = query !== null;
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  const label = t("common:nav.search_chats");

  return query === null ? (
    <div className="mb-1 flex h-8 items-center justify-between pl-2">
      <h3 className="text-[12px] font-medium text-grey">{title}</h3>
      <button
        type="button"
        data-testid="nav-section-search"
        aria-label={label}
        title={label}
        onClick={() => onQuery("")}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-grey transition-colors hover:bg-trueBlack/[0.04] hover:text-trueBlack dark:hover:bg-trueWhite/5 dark:hover:text-white"
      >
        <Search size={16} />
      </button>
    </div>
  ) : (
    <div className="flex h-8 items-center rounded-lg border border-black-300 text-grey">
      <Search size={15} className="ml-2 shrink-0" />
      <input
        ref={inputRef}
        data-testid="nav-section-search-input"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && onQuery(null)}
        placeholder={label}
        aria-label={label}
        className="ml-1.5 min-w-0 flex-1 bg-transparent text-[13px] text-trueBlack outline-none placeholder:text-grey dark:text-white"
      />
      <button
        type="button"
        aria-label={t("common:nav.close_search")}
        onClick={() => onQuery(null)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg hover:text-trueBlack dark:hover:text-white"
      >
        <X size={15} />
      </button>
    </div>
  );
};

interface NavTreeProps {
  sections: MenuSection[];
  isExactActive: (item: Pick<MenuItem, "path" | "exact">) => boolean;
  isBranchActive: (item: MenuItem) => boolean;
  onItemClick: (item: MenuItem) => void | Promise<void>;
  isAuthorized: boolean;
  /** Desktop rail collapsed to 60px — render only top-level rows as icon buttons, no tree. */
  collapsed?: boolean;
  /** A group opens its sub-items in a panel beside the rail on hover (desktop), not inline (the mobile drawer). */
  flyouts?: boolean;
}

/**
 * Shared recursive renderer for the primary navigation tree. Both the desktop `SideMenu` and the
 * mobile `MobileNavDrawer` mount this so items, nesting, expand/collapse behavior, and active-trail
 * highlighting never drift between the two shells.
 *
 * Interaction (confirmed with product):
 *  - A group row NAVIGATES to its first link AND expands, ACCORDION-style — opening one dropdown
 *    collapses its siblings so the rail stays uncluttered. The chevron toggles collapse independently.
 *  - Navigating replaces the open branch with the active one (so the tree reflects where you are).
 *  - A group whose branch is active but which the user has collapsed shows the selected state itself,
 *    so you can still tell which section you're in.
 *  - Collapsed rail: hovering a group icon reveals its sub-items in a flyout (portaled, so the
 *    scroll container can't clip it).
 */
const NavTree = ({ sections, isExactActive, isBranchActive, onItemClick, isAuthorized, collapsed = false, flyouts = false }: NavTreeProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /** A searchable section's query (`null`: its search is closed). */
  const [queries, setQueries] = useState<Record<number, string | null>>({});
  const matching = (section: MenuSection): MenuItem[] => {
    const query = queries[section.index]?.trim().toLocaleLowerCase();

    return query ? section.items.filter((item) => item.name.toLocaleLowerCase().includes(query)) : section.items;
  };

  // The collapsed rail shows icons only: a section of icon-less rows (recent chats) is its one row there.
  const railItems = useMemo(() => sections.flatMap((s) => (s.collapsedItem ? [s.collapsedItem] : s.items)), [sections]);

  // Dropdown siblings (same parent) — used to close the others when one opens (accordion). Plus a
  // key→item map so the accordion can tell which sibling is the active branch (never auto-collapsed).
  const { siblingsByKey, itemByKey } = useMemo(() => {
    const siblings: Record<string, string[]> = {};
    const byKey: Record<string, MenuItem> = {};
    const walk = (items: MenuItem[]) => {
      const dropdownKeys = items.filter((i) => i.children?.length).map((i) => i.key);
      items.forEach((i) => {
        byKey[i.key] = i;
        if (i.children?.length) {
          siblings[i.key] = dropdownKeys.filter((k) => k !== i.key);
          walk(i.children);
        }
      });
    };
    sections.forEach((s) => walk(s.items));

    return { siblingsByKey: siblings, itemByKey: byKey };
  }, [sections]);

  // On navigation, open exactly the active branch (and close everything else). Runs when the route
  // changes (isBranchActive is memoized on the pathname). A group the user opens by hand without
  // navigating is untouched (this effect doesn't re-run), so browsing stays sticky until you move.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    const walk = (items: MenuItem[]) => {
      items.forEach((item) => {
        if (item.children?.length && isBranchActive(item)) {
          next[item.key] = true;
          walk(item.children);
        }
      });
    };
    sections.forEach((s) => walk(s.items));
    setExpanded(next);
  }, [sections, isBranchActive]);

  // Open `key`, collapsing its dropdown siblings EXCEPT the one whose branch is active — the section
  // you're currently in stays open (only navigating away, via the route effect, collapses it).
  const openWithAccordion = useCallback(
    (prev: Record<string, boolean>, key: string) => {
      const next = { ...prev };
      (siblingsByKey[key] ?? []).forEach((s) => {
        if (!(itemByKey[s] && isBranchActive(itemByKey[s]))) next[s] = false;
      });
      next[key] = true;

      return next;
    },
    [siblingsByKey, itemByKey, isBranchActive]
  );

  const openExclusive = useCallback((key: string) => setExpanded((prev) => openWithAccordion(prev, key)), [openWithAccordion]);

  const toggle = useCallback((key: string) => setExpanded((prev) => (prev[key] ? { ...prev, [key]: false } : openWithAccordion(prev, key))), [openWithAccordion]);

  // Clicking a dropdown row expands it accordion-style and navigates: `openSelf` groups go to their
  // OWN landing page (e.g. Moodboards → /moodboards, which highlights the group itself); every other
  // group selects its FIRST navigable child (so that sub-item highlights). A leaf navigates to itself.
  const handleRowClick = (item: MenuItem) => {
    if (item.children?.length && item.toggleOnly) {
      // Beside the rail it is already open on hover; inline it opens and closes.
      if (!flyouts) toggle(item.key);

      return;
    }
    if (item.children?.length) {
      openExclusive(item.key);
      const target = item.openSelf && item.path ? item : firstNavigableDescendant(item);
      if (target) onItemClick(target);

      return;
    }
    if (item.path) onItemClick(item);
  };

  // ---- Hover flyout beside the rail (collapsed, or `flyouts`), portaled to escape the sidebar's overflow ----
  const [flyout, setFlyout] = useState<{ key: string; top: number; left: number } | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setFlyout(null), 150);
  };
  useEffect(() => () => cancelHide(), []);

  /** Opens `item`'s panel beside the row it hovers. */
  const openFlyout = (item: MenuItem, row: HTMLElement) => {
    cancelHide();
    const rect = row.getBoundingClientRect();
    setFlyout({ key: item.key, top: rect.top, left: rect.right + 8 });
  };
  const pickFromFlyout = (item: MenuItem) => {
    setFlyout(null);
    const target = item.children?.length ? (item.openSelf && item.path ? item : firstNavigableDescendant(item)) : item;
    if (target) onItemClick(target);
  };
  const flyoutActive = (item: MenuItem) => (item.children?.length ? isBranchActive(item) : isExactActive(item));
  const renderFlyout = (items: MenuItem[]) => {
    const flyoutItem = flyout ? items.find((i) => i.key === flyout.key) : null;

    return (
      flyout && !!flyoutItem?.children?.length && <NavFlyout items={flyoutItem.children} anchor={flyout} isActive={flyoutActive} onPick={pickFromFlyout} onEnter={cancelHide} onLeave={scheduleHide} />
    );
  };

  if (collapsed) {
    return (
      <div className="flex flex-col gap-px">
        {railItems.map((item) => {
          const disabled = !isAuthorized && item.path !== PATHS.HOME;
          const active = isBranchActive(item);
          const hasChildren = !!item.children?.length;

          return (
            <button
              key={item.key}
              type="button"
              title={item.name}
              className={clsx(
                "relative flex h-8 items-center justify-center rounded-lg transition-colors",
                active ? "bg-trueBlack/[0.06] text-trueBlack dark:bg-trueWhite/10 dark:text-white" : "text-trueBlack hover:bg-trueBlack/[0.04] dark:text-white dark:hover:bg-trueWhite/5",
                disabled && "cursor-not-allowed opacity-50"
              )}
              onMouseEnter={(e) => hasChildren && openFlyout(item, e.currentTarget)}
              onMouseLeave={() => hasChildren && scheduleHide()}
              onClick={() => {
                const target = hasChildren ? (item.openSelf && item.path ? item : firstNavigableDescendant(item)) : item;
                if (target) onItemClick(target);
              }}
            >
              {item.icon && <item.icon className="h-[18px] w-[18px] flex-shrink-0" />}
            </button>
          );
        })}

        {renderFlyout(railItems)}
      </div>
    );
  }

  const renderNode = (item: MenuItem, depth: number): React.ReactNode => {
    const hasChildren = !!item.children?.length;
    const open = !!expanded[item.key];
    // Leaves highlight when they are the current page. A dropdown highlights when its branch is active
    // but collapsed, OR — only for `openSelf` groups (Moodboards) — when the route is its OWN landing
    // page even while expanded (it has no child for that route). Non-`openSelf` groups defer to the
    // highlighted child when open, so a group like Photoshoot Assets whose own path equals its first
    // child's route (…/products) doesn't light up alongside that child.
    const active = hasChildren ? (item.openSelf && isExactActive(item)) || (isBranchActive(item) && (flyouts || !open)) : isExactActive(item);
    const disabled = !isAuthorized && item.path !== PATHS.HOME;

    return (
      <li key={item.key}>
        <div
          role="button"
          data-menu-row
          tabIndex={disabled ? -1 : 0}
          data-testid={`nav-item-${item.key}`}
          className={clsx(
            "group relative z-10 flex items-center gap-2.5 rounded-lg px-2 text-[14px] text-trueBlack cursor-pointer transition-[color,background-color,transform] duration-150 active:scale-[0.98] dark:text-white",
            // The hover is the section's gliding highlight (`GlideMenu`); while it is on the list, it stands in for the selection too.
            // A row with a subtitle (a recent chat's canvas) takes its two lines.
            item.subtitle ? "min-h-8 py-1" : "h-8",
            active && "bg-trueBlack/[0.06] group-hover/glide:bg-transparent dark:bg-trueWhite/10",
            disabled && "cursor-not-allowed opacity-50"
          )}
          style={depth > 0 ? { paddingLeft: 8 + depth * 16 } : undefined}
          {...(flyouts && hasChildren
            ? {
                onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => openFlyout(item, e.currentTarget),
                onMouseLeave: scheduleHide,
                "aria-haspopup": "menu" as const,
                "aria-expanded": flyout?.key === item.key,
              }
            : {})}
          onClick={() => handleRowClick(item)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleRowClick(item);
            }
          }}
        >
          {item.icon ? (
            // The prototype's geometry: an 18px icon in a 20px box, the label 10px after it.
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <item.icon className="h-[18px] w-[18px]" />
            </span>
          ) : depth === 0 ? null : (
            // Leaf marker keeps sub-item labels aligned with their icon-bearing ancestors.
            <span className="flex-shrink-0 w-1 h-1 rounded-full bg-current opacity-40" aria-hidden />
          )}
          {item.subtitle ? (
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate">{item.name}</span>
              <span className="truncate text-[12px] text-grey">{item.subtitle}</span>
            </span>
          ) : (
            <span className="truncate flex-1">{item.name}</span>
          )}
          {hasChildren && flyouts && <ChevronRight size={16} className="shrink-0 text-grey" aria-hidden />}
          {hasChildren && !flyouts && (
            <span
              role="button"
              aria-label={item.name}
              tabIndex={-1}
              className="flex-shrink-0 -mr-1 p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                toggle(item.key);
              }}
            >
              <IconChevronDown className={clsx("w-4 h-4 transition-transform", open ? "" : "-rotate-90")} />
            </span>
          )}
        </div>

        {hasChildren && !flyouts && (
          <div className={clsx("grid transition-[grid-template-rows] duration-200 ease-in-out", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
            <div className="overflow-hidden">
              <ul className="flex flex-col w-full gap-px mt-px">{item.children?.map((child) => renderNode(child, depth + 1))}</ul>
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      {sections.map((section, index) => (
        <React.Fragment key={section.index}>
          <div className="flex flex-col">
            {section.searchable && section.title ? (
              <SectionSearch title={section.title} query={queries[section.index] ?? null} onQuery={(query) => setQueries((current) => ({ ...current, [section.index]: query }))} />
            ) : (
              section.title && <h3 className="flex items-center px-2 pb-0.5 pt-1 text-[12px] font-medium text-grey">{section.title}</h3>
            )}
            <GlideMenu className="group/glide" highlightClassName="inset-x-0 rounded-lg bg-trueBlack/[0.06] dark:bg-trueWhite/10">
              <ul className="flex flex-col w-full gap-px">{matching(section).map((item) => renderNode(item, 0))}</ul>
            </GlideMenu>
          </div>
          {index < sections.length - 1 && <div className="mx-2 my-2 border-t border-black-300" />}
        </React.Fragment>
      ))}
      {flyouts && renderFlyout(sections.flatMap((section) => section.items))}
    </>
  );
};

export default NavTree;
